/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { formatAuditEventLabel, type AuditLogRow } from '../../../shell/audit/auditLogs';
import { categorizeEventType, AUDIT_CATEGORY_LABELS } from '../../../shell/audit/auditCategories';
import { getAuditListSubtitle } from '../../../shell/audit/auditLogPresentation';
import { formatAuditWhen } from '../../teamUtils';
import { SURFACE_CARD_RADIUS_CONTENT } from '../../../shared/surface-cards/surfaceCards.styles';

type Props = {
  rows: AuditLogRow[];
  resolveUserLabel: (userId: string | null) => string | null;
  onSelectRow?: (row: AuditLogRow) => void;
};

export function TeamHistoryTable({ rows, resolveUserLabel, onSelectRow }: Props) {
  if (rows.length === 0) {
    return (
      <View style={styles.emptyRow}>
        <Ionicons name="document-text-outline" size={28} color={ticketColors.ink3} />
        <Text style={styles.emptyText}>No hay eventos registrados con los filtros actuales.</Text>
      </View>
    );
  }
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.scroll}>
      <View style={styles.table}>
        <View style={styles.headerRow}>
          <Text style={[styles.headerCell, styles.colDate]}>Fecha</Text>
          <Text style={[styles.headerCell, styles.colUser]}>Usuario</Text>
          <Text style={[styles.headerCell, styles.colEvent]}>Evento</Text>
          <Text style={[styles.headerCell, styles.colDetail]}>Detalle</Text>
        </View>
        {rows.map((row) => {
          const subtitle = getAuditListSubtitle(row);
          const userLabel = resolveUserLabel(row.user_id) ?? '—';
          const category = categorizeEventType(row.event_type);
          const categoryLabel = AUDIT_CATEGORY_LABELS[category];
          return (
            <TouchableOpacity
              key={row.id}
              style={styles.dataRow}
              onPress={() => onSelectRow?.(row)}
              disabled={!onSelectRow}
              activeOpacity={0.7}
            >
              <View style={styles.colDate}>
                <Text style={styles.dateDay} numberOfLines={1}>{formatAuditWhen(row.created_at)}</Text>
              </View>
              <View style={styles.colUser}>
                <Text style={styles.userName} numberOfLines={1}>{userLabel}</Text>
              </View>
              <View style={styles.colEvent}>
                <Text style={styles.eventLabel} numberOfLines={1}>{formatAuditEventLabel(row.event_type)}</Text>
                <Text style={styles.eventCategory} numberOfLines={1}>{categoryLabel}</Text>
              </View>
              <View style={styles.colDetail}>
                {subtitle ? (
                  <Text style={styles.detailText} numberOfLines={2}>{subtitle}</Text>
                ) : (
                  <Text style={styles.detailEmpty}>—</Text>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flexGrow: 0,
  },
  table: {
    width: '100%',
    minWidth: 820,
    borderRadius: SURFACE_CARD_RADIUS_CONTENT,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
    backgroundColor: ticketColors.paper2,
  },
  headerCell: {
    ...typography.caption,
    fontWeight: '900',
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 52,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  colDate: {
    width: 180,
    paddingRight: spacing.sm,
    justifyContent: 'center',
  },
  colUser: {
    width: 200,
    paddingRight: spacing.sm,
    justifyContent: 'center',
  },
  colEvent: {
    width: 240,
    paddingRight: spacing.sm,
    gap: 2,
    justifyContent: 'center',
  },
  colDetail: {
    flex: 1,
    minWidth: 200,
    justifyContent: 'center',
  },
  dateDay: {
    ...typography.caption,
    fontSize: 12,
    fontFamily: 'monospace',
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  userName: {
    ...typography.body,
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  eventLabel: {
    ...typography.body,
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  eventCategory: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '600',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  detailText: {
    ...typography.body,
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.ink2,
  },
  detailEmpty: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  emptyRow: {
    padding: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
  },
  emptyText: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
});
